import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import toast from '../../../utils/toast';
import { LogoMark } from '../../logo/logo-mark';
import { RightArrow } from '../../icons/right-arrow';
import { LeftArrow } from '../../icons/left-arrow';
import { useAuth } from '../../../context/auth-provider';
import { OnboardingProgress } from '../onboarding-progress';
import { AutoHeight, Collapse, Shake, StatusLabel, StepDirection, StepTransition } from '../../../motion';
import { FormSkeleton, LoadError } from '../../pages/dashboard/dashboard-shared-components/load-state';
import { Statement } from '../../pages/stays/properties/properties.styled';
import {
  getStaysSignupConfig,
  joinStaysWaitlist,
  postStaysSignup,
  refusalOf,
  StaysSignupBody,
  StaysSignupConfig,
  StaysSignupCreated,
  StaysSignupPlan,
} from '../../../api/stays-signup-client';
import { StripeCardPanel, StripeCardConfirmation, StripeIntent } from '../../payments/stripe-card-modal';
import { palette, spacing, typography } from '../../../style-utils/theme';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  Field,
  FieldRow,
  Label,
  Input,
  PasswordWrap,
  PasswordInput,
  PasswordToggle,
  FullWidthButton,
  PrimaryButton,
  SecondaryButton,
  ButtonRow,
  STEP_LAYER_STYLE,
  FieldError,
  FieldHint,
  HelperText,
  TopLink,
  SuccessBlock,
  SuccessIcon,
  InfoBanner,
} from '../onboarding.styled';

/**
 * SIGN UP FOR LOBBERO STAYS — /signup/stays (Independent Stays plan IS3).
 *
 * For hosts and hotels who do not use Lobbero: an account (name, e-mail,
 * password), the Lobbero Stays terms and data processing agreement, and then
 * the setup guide, whose first step adds their first property (IS2). One
 * card, the onboarding shell, the same primitives as /signup.
 *
 * The server decides everything (lobbero_be/src/stays/signup.js): whether
 * sign-up is open (closed until launch: the page then offers the waitlist),
 * the current wording versions, the duplicate-address answer. With Stays
 * billing on (IS4, `config.billing`) a PLAN step follows the account (per
 * place to stay, or per completed check-in, and how many places), and after
 * the agreement a CARD step saves a card for after the trial — unless the
 * server is in FREE_SIGNUP, when the plan is complimentary and no card is
 * asked. Every amount and the trial length come from the config.
 *
 * SIGNED IN ONLY ON "SET UP YOUR FIRST LISTING". /signup/* bounces a signed-in
 * visitor home, so adopting the session the moment the account exists would
 * yank the person off the "You're in" screen (the /signup wizard's rule).
 */
type StepKey = 'account' | 'plan' | 'agreement' | 'card' | 'done';
// The steps this server asks for (IS4): plan with billing, card unless free.
const orderOf = (c: StaysSignupConfig | null): StepKey[] => {
  if (!c || !c.billing) return ['account', 'agreement'];

  return c.free_signup ? ['account', 'plan', 'agreement'] : ['account', 'plan', 'agreement', 'card'];
};

// Money as the server sent it: integer cents in the plan's currency.
const money = (cents: number, currency = 'usd'): string =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

const PlanCard = styled('button', { shouldForwardProp: prop => prop !== 'chosen' })<{ chosen: boolean }>`
  display: grid;
  gap: 4px;
  width: 100%;
  min-height: 44px;
  text-align: left;
  padding: ${spacing.md};
  border-radius: 12px;
  border: 2px solid ${p => (p.chosen ? palette.lagoon : palette.lineStrong)};
  background: ${p => (p.chosen ? palette.tealTintSoft : palette.surface)};
  color: ${palette.ink};
  font-family: ${typography.ui};
  cursor: pointer;
  & strong {
    font-size: ${typography.size.md}px;
  }
  & span {
    font-size: ${typography.size.sm}px;
    color: ${palette.slate};
  }
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AccountForm {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  business_name: string;
  phone_number: string;
}
const EMPTY: AccountForm = { first_name: '', last_name: '', email: '', password: '', business_name: '', phone_number: '' };

// A copy of an error map without one field's message.
const without = (e: Record<string, string>, key: string): Record<string, string> => {
  const next = { ...e };
  delete next[key];

  return next;
};

const Legal = styled.div`
  display: grid;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};

  & a {
    color: ${palette.lagoon};
    font-weight: ${typography.weight.semibold};
  }
`;

// S2 (IS8): the honeypot. Off screen and hidden from screen readers: a person never sees or
// fills it; a form-filling bot does, and the server refuses the sign-up.
const Honeypot = styled.div`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const Small = styled.p`
  margin: ${spacing.xs} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

export const StaysSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [config, setConfig] = useState<StaysSignupConfig | null>(null);
  const [step, setStep] = useState<StepKey>('account');
  const [direction, setDirection] = useState<StepDirection>('forward');
  const [attempt, setAttempt] = useState(0);
  const [form, setForm] = useState<AccountForm>(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [accept, setAccept] = useState({ terms: false, dpa: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<StaysSignupCreated | null>(null);
  // IS4: the plan step.
  const [planCode, setPlanCode] = useState<string>('');
  const [properties, setProperties] = useState('1');
  // The account the card step created, kept until Stripe confirms the card.
  const pendingCreated = useRef<StaysSignupCreated | null>(null);
  // Kept in state: the account step unmounts before the account is created.
  const [honeypot, setHoneypot] = useState('');
  const [waitEmail, setWaitEmail] = useState('');
  const [waitState, setWaitState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [waitError, setWaitError] = useState('');
  const seq = useRef(0);

  const load = useCallback(() => {
    const mine = ++seq.current;
    setView('loading');
    getStaysSignupConfig()
      .then(c => {
        if (mine !== seq.current) return;
        setConfig(c);
        setView('ready');
      })
      .catch(() => {
        if (mine === seq.current) setView('error');
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k: keyof AccountForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors(e => without(e, k));
  };

  const go = (to: StepKey, dir: StepDirection) => {
    setDirection(dir);
    setStep(to);
  };

  // The checks a person can see before anything is sent. The server repeats
  // every one of them (and the password policy) and has the last word.
  const checkAccount = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.first_name.trim()) next.first_name = 'Enter your first name.';
    if (!EMAIL_RE.test(form.email.trim())) next.email = 'Enter a valid email address.';
    if (form.password.length < 8) next.password = 'Use at least 8 characters.';
    setErrors(next);
    if (Object.keys(next).length) {
      setAttempt(a => a + 1);

      return false;
    }

    return true;
  };

  const plan: StaysSignupPlan | null = (config && config.plans.find(p => p.code === planCode)) || null;
  const propertyCount = Number(properties);
  const maxProperties = (config && config.max_properties) || 50;
  const checkPlan = (): boolean => {
    const next: Record<string, string> = {};
    if (!plan) next.plan_code = 'Choose a plan.';
    if (!(Number.isInteger(propertyCount) && propertyCount >= 1 && propertyCount <= maxProperties)) {
      next.properties = `Choose from 1 to ${maxProperties} places to stay.`;
    }
    setErrors(next);
    if (Object.keys(next).length) {
      setAttempt(a => a + 1);

      return false;
    }

    return true;
  };

  const body = (): StaysSignupBody => ({
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim(),
    password: form.password,
    ...(form.phone_number.trim() ? { phone_number: form.phone_number.trim() } : {}),
    ...(form.business_name.trim() ? { business_name: form.business_name.trim() } : {}),
    accept: {
      terms: accept.terms,
      dpa: accept.dpa,
      terms_version: config ? config.legal.terms_version : '',
      dpa_version: config ? config.legal.dpa_version : '',
    },
    ...(config && config.billing ? { plan_code: planCode, properties: propertyCount } : {}),
    // S2 (IS8): the bot check.
    ...(config && config.form_token ? { form_token: config.form_token } : {}),
    ...(honeypot ? { website: honeypot } : {}),
  });

  // A refusal from POST /signup/stays, mapped to the step that can fix it.
  // Returns true when it was shown on a step (the card panel stays quiet).
  const showRefusal = (err: unknown): boolean => {
    const r = refusalOf(err);
    if (r.status === 409 && r.code === 'LEGAL_VERSION_CHANGED' && r.legal) {
      // The wording changed while the page was open: show the new versions
      // and ask again — an old tick is not an acceptance of a new text.
      setConfig(c => (c ? { ...c, legal: r.legal as StaysSignupConfig['legal'] } : c));
      setAccept({ terms: false, dpa: false });
      setErrors({ terms: r.message });
      go('agreement', 'back');
    } else if (r.status === 409 && !r.code) {
      setErrors({ email: 'An account with this email already exists. Sign in instead.' });
      go('account', 'back');
    } else if (r.code === 'INVALID_FIELDS' || r.code === 'UNKNOWN_FIELDS') {
      setErrors(r.fields);
      go(r.fields.plan_code || r.fields.properties ? 'plan' : 'account', 'back');
    } else if (r.code === 'PLAN_NOT_FOUND' || r.code === 'PLAN_NOT_PURCHASABLE') {
      setErrors({ plan_code: r.message });
      go('plan', 'back');
    } else if (r.code === 'ACCEPT_REQUIRED') {
      setErrors(Object.fromEntries(r.missing.map(k => [k, r.message])));
      go('agreement', 'back');
    } else if (r.status === 409 && r.code === 'FORM_EXPIRED' && r.form_token) {
      // S2: the page was open for hours. Take the fresh token; one more press sends it.
      const fresh = r.form_token;
      setConfig(c => (c ? { ...c, form_token: fresh } : c));
      setErrors({ terms: r.message });
      go('agreement', 'back');
    } else if (r.code === 'STAYS_SIGNUP_CLOSED') {
      setConfig(c => (c ? { ...c, mode: 'closed' } : c));
    } else {
      return false;
    }
    setAttempt(a => a + 1);

    return true;
  };

  // The agreement's button: with a card step it moves on (the card step
  // creates the account); otherwise it creates the account now.
  const agree = () => {
    const missing: Record<string, string> = {};
    if (!accept.terms) missing.terms = 'Please accept the Lobbero Stays terms.';
    if (!accept.dpa) missing.dpa = 'Please accept the data processing agreement.';
    if (Object.keys(missing).length) {
      setErrors(missing);
      setAttempt(a => a + 1);

      return;
    }
    if (orderOf(config).includes('card')) go('card', 'forward');
    else submit();
  };

  // The card step's intent: the account and its trialing plan are created
  // here (Stripe first on the server), and the SetupIntent saves the card.
  const prepareIntent = async (): Promise<StripeIntent> => {
    const out = await postStaysSignup(body());
    if (!out || out.account_created !== true || !out.user || !out.payment || !out.payment.client_secret) {
      throw new Error('Sign-up finished without a payment setup. Please contact support.');
    }
    pendingCreated.current = out;

    return { mode: 'setup', clientSecret: out.payment.client_secret };
  };
  const onCardConfirmed = async (result: StripeCardConfirmation) => {
    // A trial saves a card; it never charges one.
    if (result.confirmed !== 'confirmCardSetup' || !pendingCreated.current) {
      throw new Error('Unexpected payment confirmation on a free trial. Nothing was completed — please contact support.');
    }
    setCreated(pendingCreated.current);
    go('done', 'forward');
  };

  const submit = async () => {
    if (!config) return;
    const missing: Record<string, string> = {};
    if (!accept.terms) missing.terms = 'Please accept the Lobbero Stays terms.';
    if (!accept.dpa) missing.dpa = 'Please accept the data processing agreement.';
    if (Object.keys(missing).length) {
      setErrors(missing);
      setAttempt(a => a + 1);

      return;
    }
    setSubmitting(true);
    try {
      const out = await postStaysSignup(body());
      if (!out || out.account_created !== true || !out.user) throw new Error('no account');
      setCreated(out);
      go('done', 'forward');
    } catch (err) {
      if (!showRefusal(err)) {
        toast.error(refusalOf(err).message || 'Could not create your account. Please try again.');
        setAttempt(a => a + 1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const finish = () => {
    if (!created) return;
    signIn(created.user, created.onboarding || null, created.permissions || []);
    navigate(created.next || '/dashboard/stays/setup');
  };

  const joinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(waitEmail.trim())) {
      setWaitError('Enter a valid email address.');

      return;
    }
    setWaitError('');
    setWaitState('busy');
    try {
      await joinStaysWaitlist(waitEmail.trim());
      setWaitState('done');
    } catch (err) {
      setWaitError(refusalOf(err).message || 'Could not add you to the list. Please try again.');
      setWaitState('idle');
    }
  };

  // With billing on, sign-up opens only when the server offers a plan.
  const open = !!config && config.mode === 'open' && (!config.billing || config.plans.length > 0);
  const ORDER = orderOf(config);
  const stepIndex = step === 'done' ? ORDER.length : ORDER.indexOf(step) + 1;
  const trialWords = config && config.trial_days ? `${config.trial_days}-day free trial` : 'trial';
  const monthly = plan ? plan.price_cents * (Number.isInteger(propertyCount) ? propertyCount : 0) : 0;

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero Stays</Wordmark>
        </BrandRow>

        {view === 'loading' ? (
          <FormSkeleton fields={4} label="sign-up" />
        ) : view === 'error' || !config ? (
          <LoadError message="Could not open sign-up." onRetry={load} />
        ) : !open ? (
          <>
            <Heading>{config.mode === 'closed' ? 'Sign-up opens soon' : 'Sign-up is paused'}</Heading>
            <Subheading>
              Lobbero Stays lets guests check in online before they arrive — trip details, ID and house rules —
              so arrival is a formality. Leave your email and we will tell you when you can sign up.
            </Subheading>
            {waitState === 'done' ? (
              <InfoBanner tone="success" role="status">
                You are on the list. We will email you when sign-up opens.
              </InfoBanner>
            ) : (
              <form onSubmit={joinWaitlist} noValidate>
                <Field>
                  <Label htmlFor="sw-email">Email</Label>
                  <Input
                    id="sw-email"
                    type="email"
                    value={waitEmail}
                    onChange={e => setWaitEmail(e.target.value)}
                    hasError={!!waitError}
                    autoComplete="email"
                  />
                  <FieldError message={waitError} />
                </Field>
                <FullWidthButton type="submit" disabled={waitState === 'busy'}>
                  <StatusLabel
                    state={waitState === 'busy' ? 'busy' : 'idle'}
                    labels={{ idle: 'Join the waitlist', busy: 'Adding…' }}
                  />
                </FullWidthButton>
              </form>
            )}
            <TopLink>
              Already have an account? <Link to="/">Sign in</Link>
            </TopLink>
          </>
        ) : (
          <AutoHeight>
            <Collapse open={step !== 'done'}>
              <OnboardingProgress current={stepIndex} total={ORDER.length} />
            </Collapse>

            <StepTransition stepKey={step} direction={direction} style={STEP_LAYER_STYLE}>
              {step === 'account' && (
                <>
                  <Heading>Create your account</Heading>
                  <Subheading>
                    For hosts, property managers and hotels. Your guests check in online before they arrive.
                  </Subheading>
                  <FieldRow>
                    <Field>
                      <Label htmlFor="ss-first">First name</Label>
                      <Shake active={!!errors.first_name} attempt={attempt}>
                        <Input
                          id="ss-first"
                          value={form.first_name}
                          onChange={set('first_name')}
                          hasError={!!errors.first_name}
                          autoComplete="given-name"
                        />
                      </Shake>
                      <FieldError message={errors.first_name} />
                    </Field>
                    <Field>
                      <Label htmlFor="ss-last">Last name</Label>
                      <Input
                        id="ss-last"
                        value={form.last_name}
                        onChange={set('last_name')}
                        hasError={!!errors.last_name}
                        autoComplete="family-name"
                      />
                      <FieldError message={errors.last_name} />
                    </Field>
                  </FieldRow>
                  <Field>
                    <Label htmlFor="ss-email">Email</Label>
                    <Shake active={!!errors.email} attempt={attempt}>
                      <Input
                        id="ss-email"
                        type="email"
                        value={form.email}
                        onChange={set('email')}
                        hasError={!!errors.email}
                        autoComplete="email"
                      />
                    </Shake>
                    <FieldError message={errors.email} />
                  </Field>
                  <Honeypot aria-hidden="true">
                    <label htmlFor="ss-website">Website</label>
                    <input id="ss-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
                  </Honeypot>
                  <Field>
                    <Label htmlFor="ss-pass">Password</Label>
                    <Shake active={!!errors.password} attempt={attempt}>
                      <PasswordWrap>
                        <PasswordInput
                          id="ss-pass"
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          onChange={set('password')}
                          hasError={!!errors.password}
                          autoComplete="new-password"
                        />
                        <PasswordToggle
                          type="button"
                          onClick={() => setShowPassword(s => !s)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </PasswordToggle>
                      </PasswordWrap>
                    </Shake>
                    <FieldError message={errors.password} />
                    {!errors.password ? <FieldHint>At least 8 characters.</FieldHint> : null}
                  </Field>
                  <Field>
                    <Label htmlFor="ss-business">Business name (optional)</Label>
                    <Input
                      id="ss-business"
                      value={form.business_name}
                      onChange={set('business_name')}
                      hasError={!!errors.business_name}
                      autoComplete="organization"
                    />
                    <FieldError message={errors.business_name} />
                  </Field>
                  <Field>
                    <Label htmlFor="ss-phone">Phone (optional)</Label>
                    <Input
                      id="ss-phone"
                      type="tel"
                      value={form.phone_number}
                      onChange={set('phone_number')}
                      hasError={!!errors.phone_number}
                      autoComplete="tel"
                    />
                    <FieldError message={errors.phone_number} />
                  </Field>
                  <FullWidthButton
                    type="button"
                    onClick={() => checkAccount() && go(config.billing ? 'plan' : 'agreement', 'forward')}
                  >
                    Continue <RightArrow />
                  </FullWidthButton>
                  <TopLink>
                    Already have an account? <Link to="/">Sign in</Link>
                  </TopLink>
                </>
              )}

              {step === 'plan' && (
                <>
                  <Heading>Your plan</Heading>
                  <Subheading>
                    {config.free_signup
                      ? 'Lobbero Stays is complimentary while we are in early access. Choose how you would pay later.'
                      : `Nothing is charged today. Your ${trialWords} starts when your account is made.`}
                  </Subheading>
                  <Shake active={!!errors.plan_code} attempt={attempt}>
                    <div role="radiogroup" aria-label="Plan" style={{ display: 'grid', gap: spacing.sm }}>
                      {config.plans.map(p => (
                        <PlanCard
                          key={p.code}
                          type="button"
                          role="radio"
                          aria-checked={planCode === p.code}
                          chosen={planCode === p.code}
                          data-plan-code={p.code}
                          onClick={() => {
                            setPlanCode(p.code);
                            setErrors(e => without(e, 'plan_code'));
                          }}
                        >
                          <strong>
                            {p.model === 'per_checkin'
                              ? `${money(p.fee_cents, p.currency)} per completed check-in`
                              : `${money(p.price_cents, p.currency)} per place to stay / month`}
                          </strong>
                          <span>
                            {p.model === 'per_checkin'
                              ? 'No monthly fee per place. You pay when a guest completes their online check-in.'
                              : 'One price per place to stay, however many guests check in.'}
                          </span>
                        </PlanCard>
                      ))}
                    </div>
                  </Shake>
                  <FieldError message={errors.plan_code} />
                  <Field>
                    <Label htmlFor="ss-properties">How many places to stay?</Label>
                    <Input
                      id="ss-properties"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={maxProperties}
                      value={properties}
                      onChange={e => {
                        setProperties(e.target.value);
                        setErrors(er => without(er, 'properties'));
                      }}
                      hasError={!!errors.properties}
                    />
                    <FieldHint>Each apartment or whole place counts as one. You can add more later on Billing.</FieldHint>
                    <FieldError message={errors.properties} />
                  </Field>
                  {plan && plan.model === 'per_listing' && Number.isInteger(propertyCount) && propertyCount >= 1 && (
                    <HelperText data-testid="ss-plan-total">
                      {money(monthly, plan.currency)} / month{config.free_signup ? ' — complimentary for now' : ` after your ${trialWords}`}
                    </HelperText>
                  )}
                  {plan && plan.model === 'per_checkin' && (
                    <HelperText data-testid="ss-plan-total">
                      {money(plan.fee_cents, plan.currency)} for each completed check-in
                      {config.free_signup ? ' — complimentary for now' : ` after your ${trialWords}`}
                    </HelperText>
                  )}
                  <ButtonRow>
                    <SecondaryButton type="button" onClick={() => go('account', 'back')}>
                      <LeftArrow /> Back
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={() => checkPlan() && go('agreement', 'forward')}>
                      Continue <RightArrow />
                    </PrimaryButton>
                  </ButtonRow>
                </>
              )}

              {step === 'agreement' && (
                <>
                  <Heading>Your agreement with us</Heading>
                  <Subheading>
                    You decide what happens with your guests&rsquo; details; Lobbero handles them for you. These two
                    documents say how.
                  </Subheading>
                  <Legal>
                    <Shake active={!!errors.terms} attempt={attempt}>
                      <Statement htmlFor="ss-terms">
                        <input
                          id="ss-terms"
                          type="checkbox"
                          checked={accept.terms}
                          aria-invalid={!!errors.terms}
                          onChange={e => {
                            setAccept(a => ({ ...a, terms: e.target.checked }));
                            setErrors(e => without(e, 'terms'));
                          }}
                        />
                        <span>
                          I accept the{' '}
                          <a href={config.legal.terms_url} target="_blank" rel="noopener noreferrer">
                            Lobbero Stays terms
                          </a>
                          .
                        </span>
                      </Statement>
                    </Shake>
                    <FieldError message={errors.terms} />
                    <Shake active={!!errors.dpa} attempt={attempt}>
                      <Statement htmlFor="ss-dpa">
                        <input
                          id="ss-dpa"
                          type="checkbox"
                          checked={accept.dpa}
                          aria-invalid={!!errors.dpa}
                          onChange={e => {
                            setAccept(a => ({ ...a, dpa: e.target.checked }));
                            setErrors(e => without(e, 'dpa'));
                          }}
                        />
                        <span>
                          I accept the{' '}
                          <a href={config.legal.dpa_url} target="_blank" rel="noopener noreferrer">
                            data processing agreement
                          </a>
                          , under which Lobbero handles my guests&rsquo; details for me.
                        </span>
                      </Statement>
                    </Shake>
                    <FieldError message={errors.dpa} />
                    <Small>
                      Terms {config.legal.terms_version} · Data processing agreement {config.legal.dpa_version}. We keep
                      which versions you accepted and when.
                    </Small>
                  </Legal>
                  <HelperText>
                    {!config.billing
                      ? 'Your account starts as a free preview. Nothing is charged and no card is needed.'
                      : config.free_signup
                      ? 'Your plan is complimentary for now. No card is needed.'
                      : `Nothing is charged today. Next, save a card for after your ${trialWords}.`}
                  </HelperText>
                  <ButtonRow>
                    <SecondaryButton
                      type="button"
                      onClick={() => go(config.billing ? 'plan' : 'account', 'back')}
                      disabled={submitting}
                    >
                      <LeftArrow /> Back
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={agree} disabled={submitting}>
                      <StatusLabel
                        state={submitting ? 'busy' : 'idle'}
                        labels={{ idle: ORDER.includes('card') ? 'Continue' : 'Create account', busy: 'Creating…' }}
                      />{' '}
                      <RightArrow />
                    </PrimaryButton>
                  </ButtonRow>
                </>
              )}

              {step === 'card' && plan && (
                <>
                  <Heading>Your card</Heading>
                  <Subheading>
                    Nothing is charged today. We use this card when your {trialWords} ends.
                  </Subheading>
                  <StripeCardPanel
                    collectCard
                    prepareIntent={prepareIntent}
                    onConfirmed={onCardConfirmed}
                    onError={showRefusal}
                    billingDetails={{ name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(), email: form.email.trim() }}
                    summaryRows={[
                      { label: 'Plan', value: plan.name },
                      plan.model === 'per_checkin'
                        ? { label: 'Each completed check-in', value: money(plan.fee_cents, plan.currency) }
                        : { label: 'Places to stay', value: String(propertyCount) },
                      { label: 'Today', value: money(0, plan.currency), total: true },
                    ]}
                    submitLabel={<>Create account <RightArrow /></>}
                    busyLabel="Creating…"
                    cancelLabel={<><LeftArrow /> Back</>}
                    onCancel={() => go('agreement', 'back')}
                    fallbackErrorMessage="Could not create your account. Please try again."
                  />
                </>
              )}

              {step === 'done' && (
                <SuccessBlock>
                  <SuccessIcon aria-hidden="true">✓</SuccessIcon>
                  <Heading>You&rsquo;re in</Heading>
                  <Subheading>
                    We sent a link to {form.email.trim()} to confirm your email. Next, add your first property — it
                    takes a couple of minutes.
                  </Subheading>
                  <FullWidthButton type="button" onClick={finish}>
                    Set up your first listing <RightArrow />
                  </FullWidthButton>
                </SuccessBlock>
              )}
            </StepTransition>
          </AutoHeight>
        )}
      </Card>
    </Shell>
  );
};
